import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Download, FileText, Layers, Award, Globe2, ArrowRight, Wine, Utensils, Coffee, Gift, Award as AwardIcon, Leaf, Milk, Cookie } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICONS = { wine: Wine, utensils: Utensils, coffee: Coffee, gift: Gift, award: AwardIcon, leaf: Leaf, milk: Milk, cookie: Cookie };

export default function AnnualCatalogSection() {
  const [cat, setCat] = useState(null);

  useEffect(() => {
    axios.get(`${API}/b2b-public/catalog/current`).then((r) => setCat(r.data)).catch(() => {});
  }, []);

  if (!cat) return null;

  return (
    <section id="catalogo" data-testid="annual-catalog" className="py-24 md:py-32 bg-[#0f0a05] text-white relative overflow-hidden">
      {/* Gold accents */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 10% 20%, rgba(201,169,97,0.28) 0%, transparent 55%), radial-gradient(circle at 90% 80%, rgba(201,169,97,0.18) 0%, transparent 55%)" }} />

      <div className="container-xl relative">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          {/* Left — cover / book mockup */}
          <div className="relative">
            <div className="relative aspect-[3/4] max-w-md mx-auto lg:mx-0 shadow-[0_40px_120px_-30px_rgba(201,169,97,0.5)]">
              <img src={cat.cover_url} alt={cat.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/50" />
              <div className="absolute inset-4 border border-[#C9A961]/60" />
              <div className="absolute top-8 left-8 right-8">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold">Amazonico 3000</div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-white/60 mt-1">Catalogo Annuale</div>
              </div>
              <div className="absolute bottom-10 left-8 right-8">
                <div className="editorial-heading text-6xl md:text-7xl italic text-[#C9A961] leading-none">{cat.year_from}<span className="text-white/40 mx-1">·</span>{cat.year_to}</div>
                <div className="mt-3 h-px bg-[#C9A961]/60" />
                <div className="text-[10px] uppercase tracking-[0.35em] text-white/70 mt-3">Edizione limitata</div>
              </div>
              {/* Spine effect */}
              <div className="absolute -right-2 top-2 bottom-2 w-2 bg-gradient-to-b from-[#A88A4A] via-[#C9A961] to-[#A88A4A] opacity-80" />
            </div>
            {/* Decorative label */}
            <div className="mt-6 flex items-center justify-center lg:justify-start gap-3 text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">
              <span className="h-px w-8 bg-[#C9A961]/60" />
              <span className="font-bold">{cat.code}</span>
              <span className="h-px w-8 bg-[#C9A961]/60" />
            </div>
          </div>

          {/* Right — content */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-4 flex items-center gap-2">
              <BookOpen className="h-3.5 w-3.5" /> Catalogo Prodotti · Edizione ufficiale
            </div>
            <h2 className="editorial-heading text-4xl md:text-6xl leading-[1.05]">
              {cat.title.split("·")[0]}<br />
              <span className="italic text-[#C9A961]">2026 · 2027</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-xl">{cat.subtitle}</p>

            {/* Big numbers */}
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <Big value={cat.total_skus.toLocaleString("it-IT")} label="SKU curati" />
              <Big value={cat.total_producers.toLocaleString("it-IT")} label="Produttori" />
              <Big value={cat.categories.length} label="Filiere" />
            </div>

            {/* Categories grid */}
            <div className="mt-10 grid grid-cols-2 gap-3">
              {cat.categories.map((c) => {
                const Icon = ICONS[c.icon] || Award;
                return (
                  <div key={c.key} className="flex items-start gap-3 border border-white/10 bg-white/[0.03] p-4 hover:border-[#C9A961]/50 transition-colors">
                    <Icon className="h-4 w-4 text-[#C9A961] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white font-semibold">{c.label}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-0.5">{c.sku_count} SKU · {c.highlight}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Chapters + Languages */}
            <div className="mt-10 grid sm:grid-cols-2 gap-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-3 flex items-center gap-2"><Layers className="h-3.5 w-3.5" /> Capitoli</div>
                <ul className="space-y-1.5">
                  {cat.chapters.map((ch) => (
                    <li key={ch.n} className="flex items-baseline gap-3 text-sm text-white/80">
                      <span className="text-[#C9A961] font-bold w-6">0{ch.n}</span>
                      <span className="flex-1">{ch.title}</span>
                      <span className="text-white/40 text-xs">{ch.pages}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-3 flex items-center gap-2"><Globe2 className="h-3.5 w-3.5" /> Lingue disponibili</div>
                <div className="flex flex-wrap gap-2">
                  {cat.languages.map((l) => (
                    <span key={l} className="px-3 py-1.5 border border-white/20 text-xs uppercase tracking-[0.2em] text-white/80">{l}</span>
                  ))}
                </div>
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-3 flex items-center gap-2"><Award className="h-3.5 w-3.5" /> Featured Producers</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.featured_producers.slice(0, 6).map((p) => (
                      <span key={p} className="text-[11px] text-white/60 px-2 py-1 bg-white/5 border border-white/10">{p}</span>
                    ))}
                    <span className="text-[11px] text-[#C9A961] px-2 py-1">+{cat.total_producers - 6} altri</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="catalog-cta-download" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C9A961] hover:bg-white text-black text-[10px] uppercase tracking-[0.3em] font-bold transition-colors">
                <Download className="h-4 w-4" /> Scarica catalogo PDF
              </Link>
              <Link to="/b2b/wines" data-testid="catalog-cta-browse" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/40 text-white hover:bg-white hover:text-black text-[10px] uppercase tracking-[0.3em] font-bold transition-colors">
                <FileText className="h-4 w-4" /> Sfoglia catalogo online
              </Link>
            </div>
            <div className="mt-6 text-xs text-white/40">
              Release ufficiale: 15 Gennaio 2026 · Aggiornamenti stagionali · {cat.total_skus.toLocaleString("it-IT")} SKU in {cat.categories.length} filiere premium
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Big({ value, label }) {
  return (
    <div>
      <div className="editorial-heading text-4xl md:text-5xl text-[#C9A961] leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">{label}</div>
    </div>
  );
}
